import classNames from 'classnames'
import React from 'react'
import { Bar, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import {
  EarningsColor,
  PRICE_REVISIONS_CHART_WIDTH,
  PRICE_REVISIONS_YAXIS_WIDTH,
  REVISIONS_LEFT_TICK_STYLE,
  REVISIONS_RIGHT_TICK_STYLE,
  UPDOWN_REVISIONS_CHART_HEIGHT,
  UPDOWN_REVISIONS_TICK_COUNT,
} from '../constants'
import { RevisionsChartData } from '../types'
import { getEarningsColor } from '../utils'

const CHART_GRID_COMMON_PROPS = 'absolute border-t border-secondary left-11 right-10'

function absIntFormat(value: number): string {
  return Math.abs(value).toFixed()
}

type Props = {
  activeIndex: number
  chartData: RevisionsChartData
  endingTimestamp: number
  isActive: boolean
  onSetActiveIndex: (value: number) => void
  onSetIsActive: (value: boolean) => void
  startingTimestamp: number
}

export function UpDownRevisionsChart({
  activeIndex,
  chartData,
  endingTimestamp,
  isActive,
  onSetActiveIndex,
  onSetIsActive,
  startingTimestamp,
}: Props) {
  return (
    <div className="w-full">
      <div className="relative ml-auto mr-auto flex" style={{ width: PRICE_REVISIONS_CHART_WIDTH }}>
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-0')} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '83.33%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '66.67%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '50%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '33.33%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'border-dashed')} style={{ top: '16.67%' }} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'bottom-0')} />
        <div
          style={{ width: PRICE_REVISIONS_CHART_WIDTH }}
          /**
           * Fix a problem with chart tooltip not hiding correctly on mouse leave
           * See: https://github.com/recharts/recharts/issues/2726
           */
          onMouseEnter={() => {
            onSetIsActive(true)
          }}
          onMouseLeave={() => {
            onSetIsActive(false)
          }}
        >
          <ResponsiveContainer height={UPDOWN_REVISIONS_CHART_HEIGHT}>
            <ComposedChart
              data={chartData}
              margin={{ left: 10, right: 4, top: 0, bottom: 0 }}
              stackOffset="sign"
              syncId="estimateRevisions"
              onMouseMove={(props) => {
                if (typeof props.activeTooltipIndex === 'number' && props.activeTooltipIndex !== activeIndex) {
                  onSetActiveIndex(props.activeTooltipIndex)
                }
              }}
              onMouseLeave={() => {
                onSetActiveIndex(-1)
              }}
            >
              <XAxis
                dataKey="timestamp"
                domain={[startingTimestamp, endingTimestamp]}
                hide
                scale="time"
                tick={false}
                type="number"
              />
              <YAxis
                axisLine={false}
                domain={['auto', 'auto']}
                orientation="left"
                tick={REVISIONS_LEFT_TICK_STYLE}
                tickLine={false}
                width={PRICE_REVISIONS_YAXIS_WIDTH}
                yAxisId="dummy"
              />
              <YAxis
                axisLine={false}
                domain={['auto', 'auto']}
                orientation="right"
                tick={REVISIONS_RIGHT_TICK_STYLE}
                tickCount={UPDOWN_REVISIONS_TICK_COUNT}
                tickLine={false}
                type="number"
                width={PRICE_REVISIONS_YAXIS_WIDTH}
                yAxisId="revisions"
                interval="preserveStartEnd"
                tickFormatter={absIntFormat}
              />
              <Bar
                dataKey="upRevisions"
                fill={getEarningsColor(EarningsColor.upBar)}
                isAnimationActive={false}
                maxBarSize={1}
                stackId="stack"
                yAxisId="revisions"
              ></Bar>
              <Bar
                dataKey="negDownRevisions"
                fill={getEarningsColor(EarningsColor.downBar)}
                isAnimationActive={false}
                maxBarSize={1}
                stackId="stack"
                yAxisId="revisions"
              ></Bar>
              <Tooltip
                content={() => null}
                cursor={isActive ? { stroke: getEarningsColor(EarningsColor.cursor) } : false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
